import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, map, Observable, of, startWith, switchMap, tap } from 'rxjs';
import { EmployeeStatus } from 'src/app/enums/employee-status.enum';
import { ReviewTypeEnum } from 'src/app/enums/review-type.enum';
import { PagingRequest } from 'src/app/models/paging-request.model';
import { ReviewPeriodEmployeeForEmployee } from 'src/app/models/review-period-employee-for-employee.model';
import { Search } from 'src/app/models/search.model';
import { AssessmentService, StateService } from 'src/app/services';
import { ViewHelper } from 'src/app/shared/helpers';
import { ActionStateModel, ListItem, SearchConfig, SearchOption } from 'src/app/shared/models';
import { ErrorMessageService } from 'src/app/shared/services';
import { AppState } from 'src/app/store/app.state';
import { SettingsState } from 'src/app/store/settings.state';

@UntilDestroy()
@Component({
  selector: 'app-employee-and-position-search',
  templateUrl: './employee-and-position-search.component.html',
  styleUrl: './employee-and-position-search.component.scss'
})
export class EmployeeAndPositionSearchComponent {
  private readonly itemsSubject = new BehaviorSubject<ListItem<ReviewPeriodEmployeeForEmployee>[]>([]);
  public readonly items$ = this.itemsSubject.asObservable();

  public searchControl: UntypedFormControl;

  public minCharCount = 1;
  public actionState = {editMode: false, currentItemIndex: null} as ActionStateModel;

  // paging
  public total = 0;
  public pageSize = 20;
  public pageSizeOptions = [3, 5, 20, 50, 100];
  public page = 0;
  
  public searchMessage = '';

  public employeeStatusList = EmployeeStatus;

  public selectedList: number[] = [];

  @Output()
  public add = new EventEmitter<number>();

  public get isValidCharacterCount(): boolean {
    return this.searchControl.value.length === 0 || this.searchControl.value.length >= this.minCharCount;
  }

  @ViewChild('paginator')
  paginator: MatPaginator;
    
  public get searchValue(): string {
    return this.searchControl.value;
  }

  public setPageSizeOptions(setPageSizeOptionsInput: string) {
    if (setPageSizeOptionsInput) {
      this.pageSizeOptions = setPageSizeOptionsInput.split(',').map(str => +str);
    }
  }

  highlightText = ViewHelper.highlightText;

  constructor(
      public settingState: SettingsState,
      public appState: AppState,
      private assessmentService: AssessmentService,
      private msgService: ErrorMessageService,
      private translate: TranslateService
    ) {

    }

  ngOnInit(): void {    
    this.searchControl = new UntypedFormControl('');
    
    this.searchControl.valueChanges.pipe(
        startWith(''),
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.firstPage();
          this.setValidationMessage();
        }),
        switchMap(searchString => searchString.length === 0 || searchString.length >= this.minCharCount ? this.employeeSearch(searchString): of([])),
        untilDestroyed(this)
    ).subscribe({next: itemArray => {
      this.itemsSubject.next(itemArray);
    }});
  }

  public pagingSettingsChanged(data: PageEvent)
  {
    this.page = data.pageIndex;
    this.pageSize = data.pageSize;
    this.loadList();
  }

  private employeeSearch(searchString: string): Observable<ListItem<ReviewPeriodEmployeeForEmployee>[]> {
      
      return this.assessmentService.getListForEmployeeRpe(this.buildPageRequest(searchString)).pipe(
        tap(data => {
          this.total = data.total;
          this.searchMessage = this.getResultMessage(data.list.length);
        }),
        map(result => result.list.map(x => new ListItem(x)) )
      );    
    }

  private buildPageRequest(searchString: string) {
    let data = {
      search: searchString,
      reviewType: ReviewTypeEnum.PersonalityTest,
      pagingRequestInfo: {
        pageSize: this.pageSize,
        page: this.page + 1
      } as PagingRequest
    } as Search;

    return data;
  }

  public clearText() {
    this.searchControl.setValue('');
    //this.loadList();
  }

  private loadList() {
    this.employeeSearch(this.searchControl.value)
    .pipe(untilDestroyed(this))
    .subscribe({next: itemArray => {
      this.itemsSubject.next(itemArray);
    }});
  }

  public addEmployee(option: SearchOption<ReviewPeriodEmployeeForEmployee, number>) {
    const id = option.itemSource.reviewPeriodEmployeeId;
    this.selectedList.push(id);
    this.add.emit(id);
  } 

  public removeItem(id: number) {
    this.selectedList = this.selectedList.filter(x => x !== id);
  }

  public isExists(id: number): boolean {
    return this.selectedList.includes(id);
  }

  public setValidationMessage() {
    if (!this.isValidCharacterCount)
      this.searchMessage = this.translate.instant('EMPLOYEE_AND_POSITION_SEARCH.MIN_CHAR_MESSAGE', { minCharCount: this.minCharCount });
  }

  public getResultMessage(count: number): string {
      if (this.total > count)
        return this.translate.instant('EMPLOYEE_AND_POSITION_SEARCH.RESULTS_MESSAGE', { total: this.total, count: count });
      else if (count === 0)
        return this.translate.instant('EMPLOYEE_AND_POSITION_SEARCH.NO_RESULTS');
      else
        return '';
  }
  
}
